import * as React from 'react'
import { toast } from 'sonner'

import { isViewingAs, VIEW_AS_READ_ONLY } from './view-as'

/**
 * Escrita com feedback padronizado.
 *
 * Toda escrita do Supply devolve `{ ok, reason }` (ou `{ error }` nos
 * construtores). Antes, cada botão decidia o que fazer com isso — e a maioria
 * não decidia nada: a promessa era descartada, o botão continuava ativo e uma
 * recusa do RLS parecia "cliquei e não aconteceu nada" (relatório §0
 * problema 10).
 *
 * `run()` espera, mostra o erro em toast, opcionalmente confirma o sucesso, e
 * mantém `pending` verdadeiro enquanto houver escrita em voo — é o que
 * desabilita o botão e impede o duplo clique.
 */

/** Extrai a mensagem de falha de qualquer resultado de escrita do Supply. */
export function failureOf(result: unknown): string | null {
  if (!result || typeof result !== 'object') return null
  const r = result as { ok?: unknown; reason?: unknown; error?: unknown }
  if (r.ok === false && typeof r.reason === 'string') return r.reason
  if (typeof r.error === 'string') return r.error
  return null
}

export interface WriteOptions {
  /** Toast de sucesso; sem ele, sucesso é silencioso (a tela já reflete). */
  success?: string
  /** Título do toast de erro; o motivo vindo do banco vai na descrição. */
  error?: string
}

export function useWrite() {
  const [inFlight, setInFlight] = React.useState(0)
  // "Ver como" é somente leitura: `pending` fica verdadeiro e desabilita todo
  // botão de escrita; o banco também nega (ADR 0007).
  const readOnly = isViewingAs()

  const run = React.useCallback(
    async <T>(work: Promise<T> | (() => Promise<T>), options?: WriteOptions): Promise<T> => {
      if (isViewingAs()) {
        toast.info(VIEW_AS_READ_ONLY, { id: 'ver-como-somente-leitura', description: 'Saia do "ver como" para alterar.' })
        return { ok: false, reason: VIEW_AS_READ_ONLY } as T
      }
      setInFlight((n) => n + 1)
      try {
        const result = await (typeof work === 'function' ? (work as () => Promise<T>)() : work)
        const failure = failureOf(result)
        if (failure) {
          toast.error(options?.error ?? 'Não foi possível concluir', { description: failure })
        } else if (options?.success) {
          toast.success(options.success)
        }
        return result
      } catch (caught) {
        const reason = caught instanceof Error ? caught.message : 'Falha desconhecida'
        toast.error(options?.error ?? 'Não foi possível concluir', { description: reason })
        return { ok: false, reason } as T
      } finally {
        setInFlight((n) => n - 1)
      }
    },
    [],
  )

  return { run, pending: inFlight > 0 || readOnly, saving: inFlight > 0, readOnly }
}
